import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const DemoModeContext = createContext(null);

/**
 * Scenes definition — keep in sync with backend video kit script.
 * Total: ~80 seconds (faster than the recorded video to keep attention).
 *
 * Each scene may include `highlights`: an array of timed visual cues that
 * draw a pulsing emerald frame + label around target DOM elements
 * to draw the viewer's attention during screen recording.
 *
 *   highlights: [
 *     { selector: '[data-testid="kpi-companies"]', delayMs: 1500, durationMs: 3000, label: 'KPI en direct' },
 *   ]
 */
export const DEMO_SCENES = [
  {
    id: 1,
    duration: 9,
    path: "/",
    title: { fr: "Le constat", en: "The problem" },
    narration: {
      fr: "Vos commerciaux passent 70 % de leur temps à chercher. Pas à vendre. Et si on inversait ça ?",
      en: "Your sales team spends 70% of their time searching. Not selling. What if we flipped that?",
    },
    highlights: [],
  },
  {
    id: 2,
    duration: 9,
    path: "/",
    title: { fr: "La promesse", en: "The promise" },
    narration: {
      fr: "Nexaforge Prospect Solutions, la plateforme B2B qui transforme vos mandats en deals signés, propulsée par un agent IA Claude Sonnet 4.5.",
      en: "Nexaforge Prospect Solutions — the B2B platform that turns your mandates into closed deals, powered by a Claude Sonnet 4.5 AI agent.",
    },
    highlights: [
      { selector: '[data-testid="kpi-companies"]', delayMs: 1500, durationMs: 2200, label: { fr: "Vos données en direct", en: "Live data" } },
      { selector: '[data-testid="kpi-deals"]', delayMs: 3800, durationMs: 2200, label: { fr: "Pipeline actif", en: "Active pipeline" } },
      { selector: '[data-testid="kpi-conversion"]', delayMs: 6000, durationMs: 2500, label: { fr: "Taux de conversion", en: "Conversion rate" } },
    ],
  },
  {
    id: 3,
    duration: 13,
    path: "/ai-agent",
    title: { fr: "L'Agent IA en action", en: "The AI Agent in action" },
    narration: {
      fr: "Vous indiquez un secteur, un pays. L'agent identifie les entreprises pertinentes, extrait les bons décideurs, et score chaque opportunité de 0 à 100.",
      en: "You pick a sector, a country. The agent finds the right companies, extracts the right decision-makers, and scores every opportunity from 0 to 100.",
    },
    highlights: [
      { selector: '[data-testid="ai-integrations-badges"]', delayMs: 2000, durationMs: 3000, label: { fr: "Claude · Tavily · Apollo", en: "Claude · Tavily · Apollo" } },
      { selector: '[data-testid="ai-chat-input"]', delayMs: 6000, durationMs: 4500, label: { fr: "Pose ta question à l'IA", en: "Ask the AI agent" } },
    ],
  },
  {
    id: 4,
    duration: 12,
    path: "/pipeline",
    title: { fr: "Pipelines doubles", en: "Dual pipelines" },
    narration: {
      fr: "Deux pipelines pensés pour les agents BD. Mandat pour les entités que vous représentez, Ventes pour vos prospects. Drag-and-drop fluide. Export PDF en un clic.",
      en: "Two pipelines built for BD agents. Mandate for the entities you represent, Sales for your prospects. Smooth drag-and-drop. One-click PDF export.",
    },
    highlights: [
      { selector: '[data-testid="pipeline-type-toggle"]', delayMs: 1500, durationMs: 3500, label: { fr: "Bascule Mandat / Ventes", en: "Mandate / Sales toggle" } },
      { selector: '[data-testid="pipeline-export-pdf"]', delayMs: 7000, durationMs: 3500, label: { fr: "Export PDF en 1 clic", en: "1-click PDF export" } },
    ],
  },
  {
    id: 5,
    duration: 12,
    path: "/",
    title: { fr: "AI Coach", en: "AI Coach" },
    narration: {
      fr: "L'AI Coach analyse votre pipeline en continu et vous suggère les actions qui vont vraiment vous faire closer. Un clic, c'est appliqué.",
      en: "The AI Coach continuously analyzes your pipeline and suggests the actions that will help you close. One click, applied.",
    },
    highlights: [
      { selector: '[data-testid="ai-coach-widget"]', delayMs: 1500, durationMs: 4000, label: { fr: "AI Coach toujours actif", en: "AI Coach always live" } },
      { selector: '[data-testid="ai-coach-run-now"]', delayMs: 6500, durationMs: 4500, label: { fr: "Analyse à la demande", en: "On-demand analysis" } },
    ],
  },
  {
    id: 6,
    duration: 11,
    path: "/principals",
    title: { fr: "Portail Mandant", en: "Principal Portal" },
    narration: {
      fr: "Vos Mandants ont leur propre cockpit. Ils voient leurs deals et leurs rapports PDF mensuels. Transparence totale.",
      en: "Your Principals have their own cockpit. They see their deals and monthly PDF reports. Total transparency.",
    },
    highlights: [
      { selector: '[data-testid="add-principal-btn"], [data-testid="empty-add-principal-btn"]', delayMs: 1500, durationMs: 3500, label: { fr: "Ajoutez un Mandant", en: "Add a Principal" } },
      { selector: '[data-testid="filter-principal-status"]', delayMs: 6000, durationMs: 4000, label: { fr: "Filtrez & exportez", en: "Filter & export" } },
    ],
  },
  {
    id: 7,
    duration: 9,
    path: "/",
    title: { fr: "Prêt à closer ?", en: "Ready to close?" },
    narration: {
      fr: "Nexaforge Prospect Solutions. Prospectez plus malin. Closez plus vite. Réservez votre démo dès maintenant.",
      en: "Nexaforge Prospect Solutions. Prospect smarter. Close faster. Book your demo today.",
    },
    highlights: [
      { selector: '[data-testid="dashboard-add-company"]', delayMs: 2500, durationMs: 5500, label: { fr: "Démarrez votre démo", en: "Start your demo" } },
    ],
  },
];

export function DemoModeProvider({ children }) {
  const [isActive, setIsActive] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);
  const [elapsed, setElapsed] = useState(0); // seconds within current scene
  const [paused, setPaused] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [countdown, setCountdown] = useState(0); // 3,2,1 pre-roll
  const navigate = useNavigate();
  const tickRef = useRef(null);
  const utteredScene = useRef(-1);

  // --------- Helpers ----------
  const speak = useCallback((scene, lang) => {
    if (!ttsEnabled) return;
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(scene.narration[lang] || scene.narration.fr);
      utter.lang = lang === "fr" ? "fr-FR" : "en-US";
      utter.rate = 0.96;
      utter.pitch = 1.0;
      utter.volume = 1.0;
      // Try to pick a native voice for the language
      const voices = window.speechSynthesis.getVoices() || [];
      const match =
        voices.find((v) => v.lang === utter.lang && /google|natural|premium|enhanced|microsoft/i.test(v.name)) ||
        voices.find((v) => v.lang === utter.lang) ||
        voices.find((v) => v.lang.startsWith(lang));
      if (match) utter.voice = match;
      window.speechSynthesis.speak(utter);
    } catch (_) {
      // ignore
    }
  }, [ttsEnabled]);

  const cancelSpeech = useCallback(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    }
  }, []);

  // --------- Start / Stop ----------
  const start = useCallback(() => {
    setCurrentScene(0);
    setElapsed(0);
    setPaused(false);
    utteredScene.current = -1;
    setCountdown(3);
  }, []);

  const stop = useCallback(() => {
    setIsActive(false);
    setPaused(false);
    setCurrentScene(0);
    setElapsed(0);
    setCountdown(0);
    cancelSpeech();
  }, [cancelSpeech]);

  const skipNext = useCallback(() => {
    setElapsed(0);
    utteredScene.current = -1;
    cancelSpeech();
    setCurrentScene((s) => Math.min(s + 1, DEMO_SCENES.length - 1));
  }, [cancelSpeech]);

  const skipPrev = useCallback(() => {
    setElapsed(0);
    utteredScene.current = -1;
    cancelSpeech();
    setCurrentScene((s) => Math.max(s - 1, 0));
  }, [cancelSpeech]);

  const togglePause = useCallback(() => {
    setPaused((p) => {
      if (!p) cancelSpeech();
      return !p;
    });
  }, [cancelSpeech]);

  // --------- Pre-roll countdown ----------
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => {
      if (countdown === 1) {
        setCountdown(0);
        setIsActive(true);
      } else {
        setCountdown((c) => c - 1);
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // --------- Scheduler (clean separation: tick = UI only, transition = one-shot setTimeout) ----------
  useEffect(() => {
    if (!isActive || paused) return undefined;
    if (currentScene >= DEMO_SCENES.length) {
      stop();
      return undefined;
    }
    const scene = DEMO_SCENES[currentScene];

    // Navigate
    if (scene.path) navigate(scene.path);

    // Speak narration once per scene
    if (utteredScene.current !== currentScene) {
      utteredScene.current = currentScene;
      setTimeout(() => {
        const lang = (document.documentElement.lang || "fr").startsWith("en") ? "en" : "fr";
        speak(scene, lang);
      }, 350);
    }

    // UI tick — updates the progress bar only (pure functional updater, no side-effects)
    const tickId = setInterval(() => {
      setElapsed((e) => Math.min(scene.duration, +(e + 0.1).toFixed(2)));
    }, 100);

    // Scene transition — single-shot setTimeout, fires exactly once
    const transitionId = setTimeout(() => {
      utteredScene.current = -1;
      setElapsed(0);
      setCurrentScene(currentScene + 1);
    }, scene.duration * 1000);

    return () => {
      clearInterval(tickId);
      clearTimeout(transitionId);
    };
  }, [isActive, paused, currentScene, navigate, speak, stop]);

  // Cleanup on unmount
  useEffect(() => () => cancelSpeech(), [cancelSpeech]);

  const value = {
    isActive,
    countdown,
    currentScene,
    totalScenes: DEMO_SCENES.length,
    scene: isActive ? DEMO_SCENES[currentScene] : null,
    elapsed,
    paused,
    ttsEnabled,
    setTtsEnabled,
    start,
    stop,
    skipNext,
    skipPrev,
    togglePause,
  };

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export const useDemoMode = () => {
  const ctx = useContext(DemoModeContext);
  if (!ctx) throw new Error("useDemoMode must be used within DemoModeProvider");
  return ctx;
};
